// composables/useCurrentUser.ts
//
// The signed-in user as GET /me describes them, read once per session. The InternalApi evaluates
// its own Can* policies for the caller and lists the ones that pass under `permissions`, so the app
// never maps roles to policies itself: a page or command is shown when `can` its policy.
//
// This hides what the API would refuse; it does not protect anything. Every endpoint still checks
// the policy, so when /me cannot be read the app shows everything rather than lock staff out.
import { ref } from 'vue';
import { useApiClient } from '@/composables/useApiClient';

/** The policy names of Domain/Common/Constants/Policies.cs the back office gates on. */
export type Policy =
    | 'CanEditAccount'
    | 'CanEditLedger'
    | 'CanEditAffiliate'
    | 'CanEditServiceProviderSettings'
    | 'CanEditChargePoint'
    | 'CanImportCustomers'
    | 'CanViewCall'
    | 'CanEditChargerDefinition'
    | 'CanEditContract'
    | 'CanViewContract'
    | 'CanEditDocument'
    | 'CanViewDocument'
    | 'CanEditConversionFactor'
    | 'CanEditCustomer'
    | 'CanEditPayout'
    | 'CanEditTransaction'
    | 'CanEditRegistration'
    | 'CanViewSettlementStatus'
    | 'CanViewCompliance';

const initials = ref('');
//the ConnectApp of this environment; the app is one build for staging and production, so /me says
const connectAppUrl = ref<string | null>(null);
//Production, Staging or Development as the API says; null until /me answers or when it fails, and
//the app then looks like production rather than guess
const environment = ref<string | null>(null);
//the API's Azure DevOps build, shown next to the back office's own
const apiBuildNumber = ref<string | null>(null);
const permissions = ref<ReadonlySet<string>>(new Set());
const loaded = ref(false);
const failed = ref(false);
let loading: Promise<void> | null = null;
let loadError: unknown;

/** Reads /me once; later calls share the first. Never throws: a failure sets `failed`. */
export function loadCurrentUser(): Promise<void> {
    loading ??= (async () => {
        try {
            const me = await useApiClient().me.get();
            initials.value = me?.initials ?? '';
            connectAppUrl.value = me?.connectAppUrl || null;
            environment.value = me?.environment || null;
            apiBuildNumber.value = me?.apiBuildNumber || null;
            permissions.value = new Set(me?.permissions ?? []);
        } catch (error) {
            loadError = error;
            failed.value = true;
        } finally {
            loaded.value = true;
        }
    })();
    return loading;
}

/**
 * Whether the signed-in user passes a policy. No policy always passes. Until /me has answered
 * nothing gated passes, so a forbidden menu entry never flashes; after a failed /me everything does.
 */
function can(policy?: Policy | null): boolean {
    if (!policy) return true;
    if (failed.value) return true;
    return permissions.value.has(policy);
}

/** Why /me could not be read, for the one place that reports it (the topbar). */
export function currentUserLoadError(): unknown {
    return loadError;
}

export function useCurrentUser() {
    void loadCurrentUser();
    return { initials, connectAppUrl, environment, apiBuildNumber, loaded, failed, can };
}
